import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/auth.img.png';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
    setSubmitted(true);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.875rem',
      height: '43px',
      gap: '24px',
      opacity: 1,
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(217, 217, 217, 1) !important', borderWidth: '1px !important' },
      '& input': { padding: '10px 10px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 4, sm: 6, md: 7 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 3, md: 3 },
          backgroundColor: '#ffffff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4 }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: 80, objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {/* Heading */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: '"Unbounded", sans-serif',
              fontWeight: 700,
              fontSize: '33.31px',
              lineHeight: '48px',
              letterSpacing: '0%',
              color: 'rgba(0, 0, 0, 1)',
              opacity: 0.8,
              mb: 1,
            }}
          >
            Forgot Password?
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              color: 'rgba(87, 81, 81, 1)',
            }}
          >
            {submitted
              ? 'Check your inbox for a password reset link.'
              : "Enter your email and we'll send you a reset link."}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!submitted ? (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

            {/* Email */}
            <Typography sx={labelSx}>Email address</Typography>
            <TextField
              fullWidth
              placeholder="Enter your email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              disabled={loading}
              size="small"
              sx={{ ...inputSx, mb: 3 }}
            />

            {/* Submit button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                height: '43px',
                backgroundColor: 'rgba(43, 103, 176, 1)',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: 700,
                textTransform: 'none',
                letterSpacing: 0.3,
                boxShadow: 'none',
                mb: 2.5,
                '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
                '&:disabled': { backgroundColor: '#93C5FD' },
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                  Sending...
                </Box>
              ) : 'Send Reset Link'}
            </Button>
          </Box>
        ) : (
          /* Success state — resend option */
          <Button
            fullWidth
            variant="contained"
            onClick={() => setSubmitted(false)}
            sx={{
              height: '43px',
              backgroundColor: 'rgba(43, 103, 176, 1)',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: 700,
              textTransform: 'none',
              letterSpacing: 0.3,
              boxShadow: 'none',
              mb: 2.5,
              '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
            }}
          >
            Resend Email
          </Button>
        )}

        {/* Back to login */}
        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/')}
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              color: 'rgba(43, 103, 176, 1)',
            }}
          >
            <ArrowBack sx={{ fontSize: '16px' }} />
            Back to Login
          </Link>
        </Box>
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '55%',
          height: '100%',
          backgroundColor: '#0A0F1E',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={AuthImage}
          alt="PetroTag360 dashboard preview"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
          }}
        />
      </Box>

    </Box>
  );
};

export default ForgotPasswordPage;
